/* Source Society | header search with live suggestions
   Empty box (on focus): recent searches, trending searches, popular collections, best sellers.
   While typing: query suggestions (matched text highlighted), collections, products.
   No matches: best sellers instead of a dead end.
   Data: Shopify predictive search + /collections/best-sellers/products.json. */
(() => {
  if (customElements.get('ss-search')) return;

  /* ---------- Edit these lists to change the empty-box suggestions ---------- */
  const TRENDING = ['Jordan 4', 'Essentials', 'Denim Tears', 'Yeezy 350', 'Dunk Low', 'ASICS Kayano', 'New Balance 9060', 'Travis Scott'];
  const POPULAR = [
    ['Latest arrivals', '/collections/latest-arrivals'],
    ['Best sellers', '/collections/best-sellers'],
    ['Jordan', '/collections/all-aj'],
    ['Essentials', '/collections/fog'],
    ['Denim Tears', '/collections/denim-tears'],
    ['Yeezy', '/collections/yzys'],
    ['New Balance', '/collections/new-balance'],
    ['ASICS', '/collections/asics']
  ];
  const BEST_SELLERS_URL = '/collections/best-sellers/products.json?limit=12';
  const RECENT_KEY = 'ss-recent-searches';

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const withRoot = (p) => (p.startsWith('/') ? ROOT.replace(/\/$/, '') + p : p);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const money = (value) => {
    const n = parseFloat(value);
    if (isNaN(n)) return '';
    try {
      const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cur }).format(n);
    } catch (e) {
      return n.toFixed(2);
    }
  };
  const imgUrl = (src, w) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=' + w : '');

  /* "JORDAN 4 'BLACK CAT' (2025)" -> name + colourway, same rule as the cards */
  const splitTitle = (t) => {
    const s = String(t || '').replace(/[‘’]/g, "'");
    const i = s.indexOf("'");
    if (i < 0) return { name: s.trim(), cw: '' };
    const j = s.indexOf("'", i + 1);
    const cw = (j > i ? s.slice(i + 1, j) : s.slice(i + 1)).trim();
    const tail = j > i ? s.slice(j + 1).trim() : '';
    return {
      name: (s.slice(0, i).trim() + (tail ? ' ' + tail : '')).trim(),
      cw: cw.toLowerCase().replace(/(^|[\s/])(\S)/g, (m, a, b) => a + b.toUpperCase())
    };
  };

  const mark = (text, q) => {
    const safe = esc(text);
    const words = q.trim().split(/\s+/).filter((w) => w.length > 0).map(reEsc);
    if (!words.length) return safe;
    return safe.replace(new RegExp('(' + words.join('|') + ')', 'ig'), '<mark>$1</mark>');
  };

  const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; } };
  const setRecent = (list) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch (e) {} };
  const addRecent = (q) => {
    q = String(q || '').trim();
    if (q.length < 2) return;
    setRecent([q].concat(getRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())));
  };

  let bestPromise = null;
  const bestSellers = () => {
    if (!bestPromise) {
      bestPromise = fetch(withRoot(BEST_SELLERS_URL))
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((d) => (d.products || [])
          .filter((p) => (p.variants || []).some((v) => v.available))
          .slice(0, 6)
          .map((p) => {
            const v = p.variants.find((x) => x.available) || p.variants[0];
            return {
              title: p.title,
              url: withRoot('/products/' + p.handle),
              image: p.images && p.images[0] ? p.images[0].src : '',
              price: v.price,
              compare: v.compare_at_price,
              available: true
            };
          }))
        .catch(() => []);
    }
    return bestPromise;
  };

  const ICON_CLOCK = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><circle cx="8" cy="8" r="6.2"/><path d="M8 4.8V8l2.2 1.4" stroke-linecap="round"/></svg>';
  const ICON_TREND = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 11.5 6.2 7.3l2.6 2.6L14 4.6"/><path d="M10.2 4.6H14v3.8"/></svg>';
  const ICON_SEARCH = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="7" cy="7" r="4.6"/><path d="m10.5 10.5 3.2 3.2" stroke-linecap="round"/></svg>';
  const ICON_ARROW = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const productRow = (p, q) => {
    const { name, cw } = splitTitle(p.title);
    const onSale = parseFloat(p.compare) > parseFloat(p.price);
    return `<li><a class="ss-search__item" data-ss-nav href="${esc(p.url)}">
      <span class="ss-search__thumb">${p.image ? `<img src="${esc(imgUrl(p.image, 160))}" alt="" loading="lazy" width="80" height="80">` : ''}</span>
      <span class="ss-search__meta">
        <span class="ss-search__title">${q ? mark(name, q) : esc(name)}</span>
        ${cw ? `<span class="ss-search__cw">${q ? mark(cw, q) : esc(cw)}</span>` : ''}
        <span class="ss-search__price">${money(p.price)}${onSale ? ` <s>${money(p.compare)}</s>` : ''}${p.available === false ? ' <em>Sold out</em>' : ''}</span>
      </span>
    </a></li>`;
  };

  customElements.define('ss-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.ss-search__input');
      this.panel = this.querySelector('.ss-search__panel');
      this.form = this.querySelector('form');
      this.suggestUrl = this.dataset.suggestUrl || withRoot('/search/suggest');
      this.searchUrl = this.dataset.searchUrl || withRoot('/search');
      this.cache = new Map();
      this.active = -1;
      this.timer = null;
      this.ticket = 0;
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('aria-autocomplete', 'list');

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        const q = this.input.value.trim();
        if (!q) { this.showEmpty(); return; }
        this.timer = setTimeout(() => this.search(q), 160);
      });
      this.input.addEventListener('focus', () => {
        const q = this.input.value.trim();
        q ? this.search(q) : this.showEmpty();
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      if (this.form) this.form.addEventListener('submit', () => addRecent(this.input.value));

      this.panel.addEventListener('click', (e) => {
        const term = e.target.closest('[data-ss-term]');
        if (term) {
          e.preventDefault();
          this.input.value = term.dataset.ssTerm;
          this.input.focus();
          this.search(term.dataset.ssTerm);
          return;
        }
        if (e.target.closest('[data-ss-clear-recent]')) {
          e.preventDefault();
          setRecent([]);
          this.showEmpty();
          return;
        }
        if (e.target.closest('a[href]')) addRecent(this.input.value);
      });

      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      bestSellers(); /* warm the cache so the empty panel is instant */
    }

    /* ---------- Empty box ---------- */
    async showEmpty() {
      const ticket = ++this.ticket;
      const recent = getRecent();
      let html = '<div class="ss-search__grid ss-search__grid--empty"><div class="ss-search__side">';

      if (recent.length) {
        html += `<div class="ss-search__block"><p class="ss-search__h">Recent searches <button type="button" class="ss-search__clear" data-ss-clear-recent>Clear</button></p><ul class="ss-search__terms">`;
        recent.forEach((t) => { html += `<li><a href="#" data-ss-nav data-ss-term="${esc(t)}">${ICON_CLOCK}<span>${esc(t)}</span></a></li>`; });
        html += '</ul></div>';
      }

      html += '<div class="ss-search__block"><p class="ss-search__h">Trending</p><ul class="ss-search__terms">';
      TRENDING.slice(0, recent.length ? 4 : 6).forEach((t) => { html += `<li><a href="#" data-ss-nav data-ss-term="${esc(t)}">${ICON_TREND}<span>${esc(t)}</span></a></li>`; });
      html += '</ul></div>';

      html += '<div class="ss-search__block"><p class="ss-search__h">Popular collections</p><div class="ss-search__chips">';
      POPULAR.forEach(([label, url]) => { html += `<a class="ss-search__chip" data-ss-nav href="${esc(withRoot(url))}">${esc(label)}</a>`; });
      html += '</div></div></div>';

      html += '<div class="ss-search__main"><p class="ss-search__h">Best sellers</p><ul class="ss-search__list" data-ss-best><li class="ss-search__skel"></li><li class="ss-search__skel"></li><li class="ss-search__skel"></li></ul></div></div>';

      this.panel.innerHTML = html;
      this.active = -1;
      this.open();

      const best = await bestSellers();
      if (ticket !== this.ticket) return;
      const list = this.panel.querySelector('[data-ss-best]');
      if (!list) return;
      if (best.length) list.innerHTML = best.slice(0, 4).map((p) => productRow(p, '')).join('');
      else list.closest('.ss-search__main').remove();
    }

    /* ---------- Typing ---------- */
    async search(q) {
      const ticket = ++this.ticket;
      if (!this.cache.has(q)) {
        if (!this.panel.querySelector('.ss-search__grid--results')) {
          this.panel.innerHTML = '<p class="ss-search__msg">Searching…</p>';
          this.open();
        }
        try {
          const url = `${this.suggestUrl}.json?q=${encodeURIComponent(q)}`
            + '&resources[type]=query,product,collection'
            + '&resources[limit]=6'
            + '&resources[limit_scope]=each'
            + '&resources[options][unavailable_products]=last'
            + '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
          const res = await fetch(url);
          const data = await res.json();
          this.cache.set(q, (data && data.resources && data.resources.results) || {});
        } catch (e) {
          if (ticket !== this.ticket) return;
          this.panel.innerHTML = '<p class="ss-search__msg">Search is unavailable right now. Press Enter to see all results.</p>';
          this.open();
          return;
        }
      }
      if (ticket !== this.ticket || this.input.value.trim() !== q) return;
      this.render(q, this.cache.get(q));
    }

    async render(q, r) {
      const ticket = this.ticket;
      const queries = (r.queries || []).filter((x) => x.text && x.text.toLowerCase() !== q.toLowerCase()).slice(0, 5);
      const collections = (r.collections || []).slice(0, 5);
      const products = (r.products || []).map((p) => ({
        title: p.title,
        url: p.url,
        image: p.featured_image && p.featured_image.url,
        price: p.price,
        compare: p.compare_at_price_max,
        available: p.available
      }));
      const all = `${this.searchUrl}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last&type=product`;

      let side = '';
      if (queries.length) {
        side += '<div class="ss-search__block"><p class="ss-search__h">Suggestions</p><ul class="ss-search__terms">';
        queries.forEach((x) => { side += `<li><a href="#" data-ss-nav data-ss-term="${esc(x.text)}">${ICON_SEARCH}<span>${mark(x.text, q)}</span></a></li>`; });
        side += '</ul></div>';
      }
      if (collections.length) {
        side += '<div class="ss-search__block"><p class="ss-search__h">Collections</p><ul class="ss-search__terms ss-search__terms--cols">';
        collections.forEach((c) => { side += `<li><a data-ss-nav href="${esc(c.url)}"><span>${mark(c.title, q)}</span>${ICON_ARROW}</a></li>`; });
        side += '</ul></div>';
      }

      let main = '';
      if (products.length) {
        main = `<p class="ss-search__h">Products</p><ul class="ss-search__list">${products.map((p) => productRow(p, q)).join('')}</ul>`;
      } else {
        const best = await bestSellers();
        if (ticket !== this.ticket) return;
        main = `<p class="ss-search__none">No products match <strong>"${esc(q)}"</strong>. Check the spelling, or try a brand or model name.</p>`;
        if (best.length) main += `<p class="ss-search__h">Best sellers</p><ul class="ss-search__list">${best.slice(0, 4).map((p) => productRow(p, '')).join('')}</ul>`;
      }

      this.panel.innerHTML = `<div class="ss-search__grid ss-search__grid--results${side ? '' : ' ss-search__grid--solo'}">
        ${side ? `<div class="ss-search__side">${side}</div>` : ''}
        <div class="ss-search__main">${main}</div>
      </div>
      <a class="ss-search__all" data-ss-nav href="${all}"><span>View all results for "${esc(q)}"</span>${ICON_ARROW}</a>`;
      this.active = -1;
      this.open();
    }

    /* ---------- Keyboard ---------- */
    items() { return [...this.panel.querySelectorAll('[data-ss-nav]')]; }

    onKey(e) {
      if (e.key === 'Escape') { this.close(); this.input.blur(); return; }
      if (this.panel.hidden) return;
      const items = this.items();
      if (!items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.active = e.key === 'ArrowDown'
          ? (this.active + 1) % items.length
          : (this.active - 1 + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-active', i === this.active));
        items[this.active].scrollIntoView({ block: 'nearest' });
      }
      if (e.key === 'Enter' && this.active > -1) {
        e.preventDefault();
        items[this.active].click();
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.active = -1;
    }
  });

  /* ---------- Styles for the new panel (layered over ss-global.css) ---------- */
  const css = `
  .ss-search__panel { padding: 0; overflow-x: hidden; }
  @media (min-width: 960px) {
    .ss-search__panel { width: min(760px, calc(100vw - 48px)); max-height: min(78vh, 640px); }
  }
  .ss-search__grid { display: grid; grid-template-columns: 250px minmax(0, 1fr); }
  .ss-search__grid--solo { grid-template-columns: minmax(0, 1fr); }
  .ss-search__side { padding: 18px 16px 18px 18px; border-right: 1px solid var(--ss-line, #E6E6E4); background: var(--ss-offwhite, #F7F7F6); }
  .ss-search__main { min-width: 0; padding: 18px 14px 10px 16px; }
  .ss-search__block + .ss-search__block { margin-top: 20px; }
  .ss-search__h { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 0 0 10px; color: var(--ss-black, #0B0B0B); font-size: 10px; font-weight: 700; letter-spacing: .16em; text-transform: uppercase; }
  .ss-search__main .ss-search__h { padding-left: 8px; }
  .ss-search__clear { padding: 0; border: 0; background: none; color: var(--ss-grey, #6B6B6B); font: 500 10px var(--ss-font, Inter, sans-serif); letter-spacing: .08em; text-transform: uppercase; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
  .ss-search__terms { display: grid; gap: 0; margin: 0; padding: 0; list-style: none; }
  .ss-search__terms li { display: block; margin: 0; padding: 0; line-height: 1.2; }
  .ss-search__terms a { display: flex; align-items: center; gap: 10px; height: 32px; box-sizing: border-box; padding: 0 8px; margin: 0 -8px; border-radius: var(--ss-radius, 4px); color: #3D3D3B; font-size: 13px; text-decoration: none; }
  .ss-search__terms a svg { flex: none; width: 14px; height: 14px; color: var(--ss-grey-light, #9A9A98); }
  .ss-search__terms--cols a { justify-content: space-between; }
  .ss-search__terms--cols a svg { width: 12px; height: 12px; opacity: 0; transform: translateX(-4px); transition: opacity .15s ease, transform .15s ease; }
  .ss-search__terms a:hover, .ss-search__terms a.is-active { background: #FFFFFF; color: var(--ss-black, #0B0B0B); }
  .ss-search__terms--cols a:hover svg, .ss-search__terms--cols a.is-active svg { opacity: 1; transform: none; }
  .ss-search__side .ss-search__chips { padding: 0; margin: 0; border: 0; gap: 6px; }
  .ss-search__side .ss-search__chip { padding: 6px 10px; background: #FFFFFF; font-size: 10px; letter-spacing: .06em; }
  .ss-search__chip.is-active { border-color: var(--ss-black, #0B0B0B); }
  .ss-search__panel mark { background: none; color: var(--ss-black, #0B0B0B); font-weight: 700; }
  .ss-search__cw { margin-top: -2px; color: var(--ss-grey, #6B6B6B); font-size: 12px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .ss-search__title { text-transform: uppercase; font-size: 12px; font-weight: 600; letter-spacing: .02em; }
  .ss-search__price { display: flex; align-items: baseline; flex-wrap: wrap; gap: 0 6px; font-size: 12px; }
  .ss-search__price s { order: 2; margin: 0; font-size: 11px; }
  .ss-search__price em { order: 3; }
  .ss-search__none { margin: 0 8px 16px; color: #3D3D3B; font-size: 13px; line-height: 1.5; }
  .ss-search__skel { height: 72px; margin: 4px 8px; border-radius: var(--ss-radius, 4px); background: linear-gradient(90deg, #F2F2F0 0%, #FAFAF9 50%, #F2F2F0 100%); background-size: 200% 100%; animation: ssSkel 1.2s ease infinite; }
  @keyframes ssSkel { from { background-position: 200% 0; } to { background-position: -200% 0; } }
  .ss-search__panel > .ss-search__all { margin: 0; padding: 14px 18px; border-top: 1px solid var(--ss-line, #E6E6E4); border-radius: 0; background: #FFFFFF; position: sticky; bottom: 0; }
  .ss-search__panel > .ss-search__msg { padding: 16px 18px; }
  @media (max-width: 959px) {
    .ss-search__grid, .ss-search__grid--empty { grid-template-columns: minmax(0, 1fr); }
    .ss-search__side { padding: 14px 14px 6px; border-right: 0; border-bottom: 1px solid var(--ss-line, #E6E6E4); }
    .ss-search__grid--results .ss-search__side { order: 2; border-bottom: 0; border-top: 1px solid var(--ss-line, #E6E6E4); }
    .ss-search__main { padding: 14px 8px 6px; }
    .ss-search__block + .ss-search__block { margin-top: 14px; }
    .ss-search__side .ss-search__terms { display: flex; flex-wrap: wrap; gap: 6px; }
    .ss-search__side .ss-search__terms a { min-height: 32px; margin: 0; padding: 5px 11px; border: 1px solid var(--ss-line, #E6E6E4); border-radius: 999px; background: #FFFFFF; font-size: 12px; }
    .ss-search__side .ss-search__terms a svg { width: 12px; height: 12px; }
    .ss-search__terms--cols a svg { display: none; }
    .ss-search__side .ss-search__chips { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; margin-right: -14px; padding-right: 14px; }
    .ss-search__side .ss-search__chips::-webkit-scrollbar { display: none; }
    .ss-search__side .ss-search__chip { flex: none; }
  }`;
  const style = document.createElement('style');
  style.id = 'ss-search-css';
  style.textContent = css;
  document.head.appendChild(style);
})();

/* Mobile: header search icon toggles the inline search row */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-ss-search-toggle]');
  if (!btn) return;
  const row = document.getElementById(btn.getAttribute('aria-controls'));
  if (!row) return;
  const open = !row.classList.contains('is-open');
  row.classList.toggle('is-open', open);
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) {
    const input = row.querySelector('.ss-search__input');
    if (input) input.focus();
  } else {
    const s = row.querySelector('ss-search');
    if (s && s.close) s.close();
  }
});
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('[data-ss-search-toggle][aria-expanded="true"]').forEach((btn) => btn.click());
});
